import { API_BASE_URL, apiRequest, getCurrentAccessToken } from '@/shared/lib/api-client';
import { useAuthStore } from '@/shared/stores/auth-store';
import { motivoDoInstante } from './motivo-da-busca';
import { registrarAmostraDoRelogio } from './relogio-do-servidor';
import type {
  ChatMessage,
  ClosingReason,
  MediaUrlResponse,
  QuickReply,
  Tag,
  TicketDetails,
  TicketStatus,
  TicketsListResponse,
} from './types';

export type TicketsScope = 'mine' | 'all';

/** Ordem da lista no servidor: última atividade (padrão) ou chegada. */
export type TicketsOrder = 'atividade' | 'chegada_desc' | 'chegada_asc';

export function listTickets(
  status: TicketStatus,
  options: {
    limit?: number;
    cursor?: string | null;
    scope?: TicketsScope;
    userId?: number | null;
    departmentId?: number | null;
    /** Filtro por canal (whatsapp_numbers.id). */
    channelId?: number | null;
    /** Ordem da lista (ausente = 'atividade'). */
    order?: TicketsOrder;
    q?: string;
    onlyUnread?: boolean;
    onlyUnanswered?: boolean;
    /** O painel não vai usar o total: o servidor pula a contagem (vide a rota). */
    semTotal?: boolean;
    /** TEMPORÁRIO (motivo-da-busca.ts): causa da busca, gravada no log da API. */
    motivo?: string;
  } = {},
): Promise<TicketsListResponse> {
  const params = new URLSearchParams({ status });
  if (options.limit) params.set('limit', String(options.limit));
  if (options.cursor) params.set('cursor', options.cursor);
  if (options.scope) params.set('scope', options.scope);
  if (options.userId) params.set('user_id', String(options.userId));
  if (options.departmentId) params.set('department_id', String(options.departmentId));
  if (options.channelId) params.set('whatsapp_number_id', String(options.channelId));
  if (options.order && options.order !== 'atividade') params.set('order', options.order);
  if (options.q && options.q.trim()) params.set('q', options.q.trim());
  // Enviados só quando ligados (o backend trata ausência como false).
  if (options.onlyUnread) params.set('only_unread', 'true');
  if (options.onlyUnanswered) params.set('only_unanswered', 'true');
  if (options.semTotal) params.set('sem_total', 'true');
  // TEMPORÁRIO (motivo-da-busca.ts): empresa e usuário vão junto porque a
  // linha do log da requisição não traz nenhum dos dois.
  const eu = useAuthStore.getState().user;
  params.set('_motivo', options.motivo ?? motivoDoInstante() ?? 'sem-motivo');
  if (eu) {
    params.set('_t', String(eu.tenantId));
    params.set('_u', String(eu.id));
  }
  return apiRequest<TicketsListResponse>(`/tickets?${params.toString()}`);
}

export function getTicket(id: number): Promise<TicketDetails> {
  return apiRequest<TicketDetails>(`/tickets/${id}`);
}

/** Estado de entrega de uma mensagem de saída, como o servidor conhece. */
export interface StatusDeSaida {
  id: number;
  status_rank: number;
  sent_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  failed_at: string | null;
  error_code: string | null;
  error_title: string | null;
}

/**
 * Marca lida E devolve os status das últimas mensagens de saída da conversa.
 *
 * O POST sempre existiu em toda abertura; o que mudou foi o servidor deixar de
 * responder vazio. Os avisos de ✓/✓✓ do socket só chegam a quem está NA SALA da
 * conversa — quem estava fora ganha a bolha (sala do departamento) mas nunca o
 * status, e o relógio fica. Esta resposta é o acerto de contas na abertura,
 * de carona numa requisição que já ia acontecer. Ideia do dono.
 */
export function markTicketRead(id: number): Promise<{ statuses: StatusDeSaida[] }> {
  return apiRequest<{ statuses: StatusDeSaida[] }>(`/tickets/${id}/read`, {
    method: 'POST',
    body: {},
  });
}

/**
 * Devolve o selo de não-lido ao atendimento (o inverso do `markTicketRead`).
 *
 * Marcador de trabalho do atendente: "vi, não vou resolver agora, continue me
 * chamando". Não desfaz o ✓✓ azul que o cliente já viu — a marcação só existe
 * dentro do painel.
 */
export function markTicketUnread(id: number): Promise<{ marcado: boolean }> {
  return apiRequest<{ marcado: boolean }>(`/tickets/${id}/unread`, {
    method: 'POST',
    body: {},
  });
}

export interface TransferEvent {
  id: number;
  ticket_id: number;
  created_at: string;
  actor_user_id: number | null;
  actor_user_name: string | null;
  from_user_id: number | null;
  from_user_name: string | null;
  to_user_id: number | null;
  to_user_name: string | null;
  from_department_id: number | null;
  from_department_name: string | null;
  to_department_id: number | null;
  to_department_name: string | null;
  /** true se foi pra fila (sem atendente atribuído). */
  queued: boolean;
  /** 'atendente_offline' = devolvido pra Pendentes porque o dono ficou offline (vigia). */
  motivo?: 'atendente_offline' | null;
}

/** Reabertura de atendimento (botão Reabrir — sempre ação humana). */
export interface ReopenEvent {
  id: number;
  ticket_id: number;
  created_at: string;
  actor_user_id: number | null;
  actor_user_name: string | null;
  /** 'scheduled_open' = atendimento aberto por mensagem agendada. Ausente em
   *  cache antigo = reabertura. */
  kind?: 'reopen' | 'scheduled_open';
}

export interface ChatbotEvent {
  id: number;
  ticket_id: number;
  chatbot_name: string | null;
  status: 'running' | 'completed' | 'transferred' | 'abandoned' | 'errored';
  started_at: string;
  ended_at: string | null;
  error_message: string | null;
}

export interface MessagesPage {
  messages: ChatMessage[];
  next_cursor: number | null;
  /** Eventos de transferência do escopo (só na 1ª página). Frontend interleva
   *  cronologicamente com as messages e renderiza banner inline. */
  transfer_events?: TransferEvent[];
  /** Reaberturas do escopo (só na 1ª página) — banner inline igual transfer. */
  reopen_events?: ReopenEvent[];
  /** Execuções de chatbot do escopo (só na 1ª página). Frontend renderiza
   *  banner inline indicando quando a automação foi acionada/encerrada. */
  chatbot_events?: ChatbotEvent[];
}

export function listMessages(
  ticketId: number,
  options: {
    limit?: number;
    before?: number;
    ate?: number;
    scope?: 'ticket' | 'contact';
    /** Aberto pelo Histórico de Chats — lê qualquer atendimento da empresa. */
    origem?: 'historico';
  } = {},
): Promise<MessagesPage> {
  const params = new URLSearchParams();
  params.set('limit', String(options.limit ?? 20));
  if (options.before) params.set('before', String(options.before));
  if (options.ate) params.set('ate', String(options.ate));
  if (options.scope) params.set('scope', options.scope);
  if (options.origem) params.set('origem', options.origem);
  return apiRequest<MessagesPage>(`/tickets/${ticketId}/messages?${params.toString()}`);
}

/** Uma mensagem achada pela busca na conversa. */
export interface ResultadoDaBusca {
  id: number;
  /** Instante em epoch MICROSSEGUNDOS — o `ate` que desce a conversa até ela. */
  created_at_micros: number;
}

/** Busca na conversa: da mais nova pra mais antiga, até 500 (`truncated` passou disso). */
export function buscarNaConversa(
  ticketId: number,
  termo: string,
): Promise<{ results: ResultadoDaBusca[]; truncated: boolean }> {
  const params = new URLSearchParams({ q: termo });
  return apiRequest(`/tickets/${ticketId}/messages/search?${params.toString()}`);
}

/**
 * Resposta dos endpoints de envio. `created_at` é o carimbo que o BANCO gravou
 * — a bolha otimista o adota via `carimboMonotonico` (bug do relógio do PC
 * atrasado). Opcional pra não quebrar contra backend antigo sem o campo.
 */
export interface EnvioAceito {
  message_id: number;
  created_at?: string;
  /** Hora do servidor ao responder — amostra do desvio de relógio da aba. */
  server_now?: string;
}

export function sendMessage(input: {
  ticket_id: number;
  body: string;
  /** wamid da msg sendo respondida (citação WhatsApp). */
  reply_to_wamid?: string;
}): Promise<EnvioAceito> {
  // `server_now` é a hora do servidor gerada DURANTE esta requisição —
  // amostra do desvio de relógio da aba, justo quando a contagem do agrupador
  // começa (vide relogio-do-servidor.ts). Ausente em backend antigo → nada.
  const antes = Date.now();
  return apiRequest<EnvioAceito>('/messages', {
    method: 'POST',
    body: { ...input, type: 'text' },
  }).then((aceito) => {
    registrarAmostraDoRelogio(aceito.server_now, antes, Date.now());
    return aceito;
  });
}

/**
 * Envia template HSM aprovado (Cloud API). Usado pelo TemplatePickerModal
 * no thread quando a janela 24h fecha. Backend resolve o template
 * (name+language+components do DB), constrói os components Meta com vars
 * por HEADER/BODY separados, e publica em meta.send.template.
 */
export function sendTemplate(input: {
  ticket_id: number;
  template_id: number;
  template_variables?: Record<string, string>;
  /** Mídia do cabeçalho escolhida na hora (só neste envio). Ausente = a do template. */
  header_media_id?: number;
}): Promise<EnvioAceito> {
  return apiRequest<EnvioAceito>('/messages', {
    method: 'POST',
    body: { ...input, type: 'template' },
  });
}

export function sendMediaMessage(input: {
  ticket_id: number;
  type: 'image' | 'audio' | 'video' | 'document';
  media_id: number;
  caption?: string;
  /** wamid da msg sendo respondida (citação WhatsApp). */
  reply_to_wamid?: string;
}): Promise<EnvioAceito> {
  return apiRequest<EnvioAceito>('/messages', { method: 'POST', body: input });
}

/**
 * Revoke (delete-for-everyone) de uma mensagem outbound. Backend:
 * - Marca `deleted_at` + `deleted_by='agent'` na DB (preserva body).
 * - Publica em `wa.command.revoke` pro worker-whatsmeow enviar RevokeMessage.
 * - Emite realtime `message.deleted` pra outros agentes.
 *
 * Só funciona em canais qr_code (Cloud API não suporta delete-for-both).
 */
export function revokeMessage(messageId: number): Promise<void> {
  return apiRequest<void>(`/messages/${messageId}/revoke`, { method: 'POST' });
}

/**
 * Edita o texto de uma msg outbound (canal QR com a opção ligada, ≤15min,
 * autor ou admin). `body` vai FINAL (assinatura conforme o botão no momento
 * de salvar). A API só enfileira: o texto
 * novo chega pelo realtime `message.edited` depois do WhatsApp aceitar; se
 * recusar, vem `message.edit.failed`. 'unchanged' = mesmo texto, nada enviado.
 */
export function editMessage(
  messageId: number,
  body: string,
): Promise<{ status: 'pending' | 'unchanged' }> {
  return apiRequest<{ status: 'pending' | 'unchanged' }>(`/messages/${messageId}/edit`, {
    method: 'POST',
    body: { body },
  });
}

/**
 * Fecha na hora um pacote de texto que ainda está esperando os segundos do
 * agrupador. Idempotente no servidor: clicar de novo (ou clicar bem na hora em
 * que o cronômetro dispara) não manda a mensagem duas vezes pro cliente.
 */
export function enviarPacoteAgora(messageId: number): Promise<void> {
  return apiRequest<void>(`/messages/${messageId}/enviar-agora`, { method: 'POST' });
}

/**
 * Desiste de um pacote de texto que ainda não saiu: a mensagem some da
 * conversa e o servidor devolve o texto, pro painel pôr de volta no campo de
 * digitar. 409 "já foi enviada" = o tempo acabou antes do descarte.
 */
export function descartarPacote(messageId: number): Promise<{ message_id: number; body: string }> {
  return apiRequest<{ message_id: number; body: string }>(`/messages/${messageId}/descartar`, {
    method: 'POST',
    body: {},
  });
}

export interface EstadoDoPacote {
  batch_open_until: string | null;
  batch_held_remaining_ms: number | null;
}

/**
 * Modo inteligente do agrupador: `segurar` quando a caixa deixa de estar vazia
 * (o relógio para no que faltava), `soltar` quando esvazia (retoma dali).
 * Uma chamada por transição — nunca periódica.
 */
export function segurarPacote(messageId: number): Promise<EstadoDoPacote> {
  return apiRequest<EstadoDoPacote>(`/messages/${messageId}/segurar`, { method: 'POST' });
}
export function soltarPacote(
  messageId: number,
  opts: { keepalive?: boolean } = {},
): Promise<EstadoDoPacote> {
  return apiRequest<EstadoDoPacote>(`/messages/${messageId}/soltar`, {
    method: 'POST',
    ...(opts.keepalive && { keepalive: true }),
  });
}

export function getMediaUrl(messageId: number): Promise<MediaUrlResponse> {
  return apiRequest<MediaUrlResponse>(`/messages/${messageId}/media-url`);
}

export async function uploadMedia(
  file: Blob,
  filename?: string,
  opts?: { durationSec?: number; width?: number; height?: number; waveform?: number[] },
): Promise<{
  media_id: number;
  mime_type: string;
  size_bytes: number;
  filename: string | null;
}> {
  const fd = new FormData();
  fd.append('file', file, filename ?? (file instanceof File ? file.name : 'upload'));
  const token = getCurrentAccessToken();
  // Query params:
  //  - duration_sec: pra áudio gravado pelo Composer (AudioMessage.Seconds no
  //    whatsmeow — sem isso a bolha de áudio fica sem timer).
  //  - width/height: pra imagem (compressImage retorna dims; persistir em
  //    media_files.width/height pra frontend reservar espaço aspect-ratio
  //    na bolha outbound, igual inbound já faz).
  const params: string[] = [];
  if (opts?.durationSec && opts.durationSec > 0) {
    params.push(`duration_sec=${encodeURIComponent(opts.durationSec.toFixed(2))}`);
  }
  if (opts?.width && opts.width > 0) params.push(`width=${opts.width}`);
  if (opts?.height && opts.height > 0) params.push(`height=${opts.height}`);
  // waveform: 32 valores reais (0..1) da gravação, enviados como CSV de inteiros
  // 0..100 (compacto). Persistido em media_files.waveform → bolha mostra a onda
  // real (igual aos áudios espelhados), sem decodar no cliente.
  if (opts?.waveform && opts.waveform.length > 0) {
    const csv = opts.waveform.map((v) => Math.round(Math.max(0, Math.min(1, v)) * 100)).join(',');
    params.push(`waveform=${csv}`);
  }
  const qs = params.length > 0 ? `?${params.join('&')}` : '';
  const res = await fetch(`${API_BASE_URL}/media/upload${qs}`, {
    method: 'POST',
    body: fd,
    credentials: 'include',
    ...(token && { headers: { authorization: `Bearer ${token}` } }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `upload failed (${res.status})`);
  }
  return res.json();
}

export function acceptTicket(ticketId: number): Promise<void> {
  return apiRequest<void>(`/tickets/${ticketId}/accept`, { method: 'POST', body: {} });
}

export function queueTicket(ticketId: number): Promise<void> {
  return apiRequest<void>(`/tickets/${ticketId}/queue`, { method: 'POST', body: {} });
}

export function transferTicket(
  ticketId: number,
  input: { user_id?: number; department_id?: number; require_online?: boolean },
): Promise<void> {
  return apiRequest<void>(`/tickets/${ticketId}/transfer`, { method: 'POST', body: input });
}

export function closeTicket(
  ticketId: number,
  // Sem `closing_reason_id`: o servidor grava "Não especificado" — só passa
  // nos canais que não exigem motivo (senão responde 400).
  input: { closing_reason_id?: number; notes?: string },
): Promise<void> {
  return apiRequest<void>(`/tickets/${ticketId}/close`, { method: 'POST', body: input });
}

export function reopenTicket(ticketId: number): Promise<void> {
  return apiRequest<void>(`/tickets/${ticketId}/reopen`, { method: 'POST', body: {} });
}

/** Cria uma nota interna — fica só no painel, nunca vai pro WhatsApp do cliente. */
export function sendInternalNote(ticketId: number, body: string): Promise<EnvioAceito> {
  return apiRequest<EnvioAceito>(`/tickets/${ticketId}/notes`, {
    method: 'POST',
    body: { body },
  });
}

export interface BulkActionResult {
  succeeded: number[];
  failed: Array<{ id: number; reason: string }>;
}

export function bulkCloseTickets(input: {
  ticket_ids: number[];
  /** Ausente = sem motivo; a API grava "Não especificado" se nenhum canal exigir. */
  closing_reason_id?: number;
}): Promise<BulkActionResult> {
  return apiRequest<BulkActionResult>('/tickets/bulk-close', { method: 'POST', body: input });
}

export function bulkTagTickets(input: {
  ticket_ids: number[];
  tag_id: number;
  op: 'add' | 'remove';
}): Promise<BulkActionResult> {
  return apiRequest<BulkActionResult>('/tickets/bulk-tag', { method: 'POST', body: input });
}

export function bulkTransferTickets(input: {
  ticket_ids: number[];
  user_id?: number;
  department_id?: number;
  require_online?: boolean;
}): Promise<BulkActionResult> {
  return apiRequest<BulkActionResult>('/tickets/bulk-transfer', { method: 'POST', body: input });
}

export interface ActiveOutboundInput {
  whatsapp_number_id: number;
  contact_id?: number;
  new_contact?: { name: string; wa_id: string };
  /** Obrigatório — backend valida que é um dos depts do user. */
  department_id: number;
  /** Opcional — se ausente, ticket é criado sem mensagem inicial. */
  initial_message?:
    | { type: 'text'; body: string }
    | {
        type: 'template';
        template_id: number;
        variables?: Record<string, string>;
        /** Mídia do cabeçalho escolhida na hora (só neste envio). Ausente = a do template. */
        header_media_id?: number;
      };
}

export function createActiveOutbound(input: ActiveOutboundInput): Promise<{
  ticket_id: number;
  contact_id: number;
  message_id: number;
}> {
  return apiRequest<{ ticket_id: number; contact_id: number; message_id: number }>(
    '/tickets/active-outbound',
    { method: 'POST', body: input },
  );
}

export function checkWhatsAppNumber(
  whatsappNumberId: number,
  waId: string,
): Promise<{ exists: boolean; skipped: boolean }> {
  return apiRequest<{ exists: boolean; skipped: boolean }>(
    `/whatsapp-numbers/${whatsappNumberId}/check-number`,
    { method: 'POST', body: { wa_id: waId } },
  );
}

export function addTag(ticketId: number, tagId: number): Promise<void> {
  return apiRequest<void>(`/tickets/${ticketId}/tags`, {
    method: 'POST',
    body: { tag_id: tagId },
  });
}

export function removeTag(ticketId: number, tagId: number): Promise<void> {
  return apiRequest<void>(`/tickets/${ticketId}/tags/${tagId}`, { method: 'DELETE' });
}

export function listQuickReplies(): Promise<QuickReply[]> {
  return apiRequest<QuickReply[]>('/quick-replies');
}

/** Mensagens rápidas PESSOAIS (criadas pelo atendente no composer). Só atalho +
 *  corpo — sem grupos de acesso (pessoal já é privada). */
export function createMyQuickReply(input: {
  shortcut: string;
  body: string;
}): Promise<{ id: number }> {
  return apiRequest<{ id: number }>('/me/quick-replies', { method: 'POST', body: input });
}
export function updateMyQuickReply(
  id: number,
  input: { shortcut?: string; body?: string },
): Promise<void> {
  return apiRequest<void>(`/me/quick-replies/${id}`, { method: 'PATCH', body: input });
}
export function deleteMyQuickReply(id: number): Promise<void> {
  return apiRequest<void>(`/me/quick-replies/${id}`, { method: 'DELETE' });
}

export function listClosingReasons(): Promise<ClosingReason[]> {
  return apiRequest<ClosingReason[]>('/closing-reasons');
}

export function listTags(): Promise<Tag[]> {
  return apiRequest<Tag[]>('/tags');
}
